import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Grid,
  Button,
  Avatar,
  CircularProgress,
  Alert,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Card,
  CardContent,
  Tabs,
  Tab
} from '@mui/material';
import {
  MoreVert,
  Visibility,
  Edit,
  Delete,
  CheckCircle,
  Cancel,
  Schedule,
  Assignment,
  AssignmentTurnedIn,
  Email,
  Phone,
  Download,
  Person,
  Work,
  LocationOn,
  CalendarToday,
  AttachMoney,
  ArrowForward,
  ArrowBack,
  FilterList,
  Refresh
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const ManageApplications = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    status: '',
    stage: '',
    jobId: '',
    search: ''
  });
  const [jobs, setJobs] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [statusDialog, setStatusDialog] = useState(false);
  const [stageDialog, setStageDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [newStage, setNewStage] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [detailsDialog, setDetailsDialog] = useState(false);

  const statusOptions = [
    'submitted',
    'under_review',
    'shortlisted',
    'interview_scheduled',
    'rejected',
    'hired',
    'withdrawn'
  ];

  const stageOptions = [
    'Applied',
    'Screening',
    'Technical Assessment',
    'Interview',
    'Offer',
    'Hired'
  ];

  useEffect(() => {
    fetchApplications();
    fetchJobs();
  }, [page, rowsPerPage, filters]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage
      });

      if (filters.status) params.append('status', filters.status);
      if (filters.jobId) params.append('jobId', filters.jobId);

      const response = await axios.get(
        `http://localhost:5000/api/applications/admin/all?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const data = response.data.data || [];
      setApplications(data);
      setTotalCount(response.data.pagination?.total || 0);
      setError('');
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError('Failed to fetch applications');
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('http://localhost:5000/api/jobs', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(response.data.data || []);
    } catch (err) {
      console.error('Error fetching jobs:', err);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event, application) => {
    setAnchorEl(event.currentTarget);
    setSelectedApplication(application);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = async () => {
    if (!selectedApplication || !newStatus) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `http://localhost:5000/api/applications/admin/${selectedApplication._id}/status`,
        { 
          status: newStatus,
          notes: statusNotes 
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setStatusDialog(false);
      setNewStatus('');
      setStatusNotes('');
      fetchApplications();
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update application status');
    }
  };

  const handleStageChange = async () => {
    if (!selectedApplication || !newStage) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `http://localhost:5000/api/applications/${selectedApplication._id}/stage`,
        { 
          stage: newStage,
          notes: statusNotes 
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setStageDialog(false);
      setNewStage('');
      setStatusNotes('');
      fetchApplications();
    } catch (err) {
      console.error('Error updating stage:', err);
      setError('Failed to update application stage');
    }
  };

  const handleViewDetails = () => {
    setDetailsDialog(true);
    handleMenuClose();
  };

  const handleUpdateStatus = () => {
    setStatusDialog(true);
    setNewStatus(selectedApplication?.status || '');
    handleMenuClose();
  };

  const handleUpdateStage = () => {
    setStageDialog(true);
    setNewStage(selectedApplication?.currentStage || 'Applied');
    handleMenuClose();
  };

  const handleScheduleInterview = () => {
    navigate(`/schedule-interview/${selectedApplication._id}`);
    handleMenuClose();
  };

  const handleViewSubmissions = () => {
    navigate(`/task-submissions?applicationId=${selectedApplication._id}&candidateId=${selectedApplication.candidate?._id}`);
    handleMenuClose();
  };

  const getStatusColor = (status) => {
    const colors = {
      'submitted': 'info',
      'under_review': 'warning',
      'shortlisted': 'success',
      'interview_scheduled': 'primary',
      'rejected': 'error',
      'hired': 'success',
      'withdrawn': 'default'
    };
    return colors[status] || 'default';
  };

  const getStageColor = (stage) => {
    const colors = {
      'Applied': 'default',
      'Screening': 'info',
      'Technical Assessment': 'warning',
      'Interview': 'primary',
      'Offer': 'secondary',
      'Hired': 'success'
    };
    return colors[stage] || 'default';
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getApplicationStats = () => {
    const stats = {
      total: applications.length,
      submitted: applications.filter(a => a.status === 'submitted').length,
      underReview: applications.filter(a => a.status === 'under_review').length,
      shortlisted: applications.filter(a => a.status === 'shortlisted').length,
      hired: applications.filter(a => a.status === 'hired').length,
      rejected: applications.filter(a => a.status === 'rejected').length
    };
    return stats;
  };

  const stats = getApplicationStats();

  if (loading && applications.length === 0) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Manage Applications
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Review and manage all job applications
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 1 }}>
              <Typography variant="h4">{totalCount}</Typography>
              <Typography variant="caption" color="textSecondary">
                Total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 1 }}>
              <Typography variant="h4" color="info.main">
                {stats.submitted}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                New
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 1 }}>
              <Typography variant="h4" color="warning.main">
                {stats.underReview}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                In Review
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 1 }}>
              <Typography variant="h4" color="success.main">
                {stats.shortlisted}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Shortlisted
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 1 }}>
              <Typography variant="h4" color="success.main">
                {stats.hired}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Hired
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 1 }}>
              <Typography variant="h4" color="error.main">
                {stats.rejected}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Rejected
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Search"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Search by name or email"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Job</InputLabel>
              <Select
                value={filters.jobId}
                onChange={(e) => setFilters({ ...filters, jobId: e.target.value })}
                label="Job"
              >
                <MenuItem value="">All Jobs</MenuItem>
                {jobs.map((job) => (
                  <MenuItem key={job._id} value={job._id}>
                    {job.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="">All Status</MenuItem>
                {statusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status.replace('_', ' ').toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchApplications}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Applications Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Candidate</TableCell>
              <TableCell>Job Position</TableCell>
              <TableCell>Current Stage</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Applied Date</TableCell>
              <TableCell>Expected Salary</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {applications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body1" color="textSecondary" sx={{ py: 3 }}>
                    No applications found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              applications.map((application) => (
                <TableRow key={application._id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                        {application.candidate?.profile?.firstName?.[0] || 
                         application.candidate?.email?.[0]?.toUpperCase() || 'U'}
                      </Avatar>
                      <Box>
                        <Typography variant="body2">
                          {application.candidate?.profile?.firstName || 'Unknown'} {application.candidate?.profile?.lastName || ''}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {application.candidate?.email || 'No email'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {application.job?.title || 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {application.job?.department || ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={application.currentStage || 'Applied'}
                      size="small"
                      color={getStageColor(application.currentStage)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={application.status}
                      size="small"
                      color={getStatusColor(application.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(application.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {application.expectedSalary?.min 
                        ? `₹${application.expectedSalary.min.toLocaleString('en-IN')}/month`
                        : application.candidateProfile?.expectedSalary 
                        ? `₹${application.candidateProfile.expectedSalary.toLocaleString('en-IN')}/month`
                        : 'Not specified'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, application)}
                    >
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleUpdateStatus}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Update Status</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleUpdateStage}>
          <ListItemIcon>
            <ArrowForward fontSize="small" />
          </ListItemIcon>
          <ListItemText>Change Stage</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleViewSubmissions}>
          <ListItemIcon>
            <AssignmentTurnedIn fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Submissions</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleScheduleInterview}>
          <ListItemIcon>
            <Schedule fontSize="small" />
          </ListItemIcon>
          <ListItemText>Schedule Interview</ListItemText>
        </MenuItem>
      </Menu>

      {/* Status Update Dialog */}
      <Dialog open={statusDialog} onClose={() => setStatusDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Application Status</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                label="Status"
              >
                {statusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status.replace('_', ' ').toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Notes"
              multiline
              rows={3}
              value={statusNotes}
              onChange={(e) => setStatusNotes(e.target.value)}
              placeholder="Add notes about this status change..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(false)}>Cancel</Button>
          <Button onClick={handleStatusChange} variant="contained">
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stage Update Dialog */}
      <Dialog open={stageDialog} onClose={() => setStageDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Application Stage</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Stage</InputLabel>
              <Select
                value={newStage}
                onChange={(e) => setNewStage(e.target.value)}
                label="Stage"
              >
                {stageOptions.map((stage) => (
                  <MenuItem key={stage} value={stage}>
                    {stage}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Notes"
              multiline
              rows={3}
              value={statusNotes}
              onChange={(e) => setStatusNotes(e.target.value)}
              placeholder="Add notes about this stage change..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStageDialog(false)}>Cancel</Button>
          <Button onClick={handleStageChange} variant="contained">
            Update Stage
          </Button>
        </DialogActions>
      </Dialog>

      {/* Application Details Dialog */}
      {selectedApplication && (
        <Dialog open={detailsDialog} onClose={() => setDetailsDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Application Details</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Candidate Information</Typography>
                  <Typography variant="body2">
                    Name: {selectedApplication.candidate?.profile?.firstName} {selectedApplication.candidate?.profile?.lastName}
                  </Typography>
                  <Typography variant="body2">
                    Email: {selectedApplication.candidate?.email}
                  </Typography>
                  <Typography variant="body2">
                    Phone: {selectedApplication.candidate?.profile?.phone || 'Not provided'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Job Information</Typography>
                  <Typography variant="body2">
                    Position: {selectedApplication.job?.title}
                  </Typography>
                  <Typography variant="body2">
                    Department: {selectedApplication.job?.department}
                  </Typography>
                  <Typography variant="body2">
                    Location: {selectedApplication.job?.location}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Application Details</Typography>
                  <Typography variant="body2">
                    Applied: {formatDate(selectedApplication.createdAt)}
                  </Typography>
                  <Typography variant="body2">
                    Expected Salary: {selectedApplication.expectedSalary?.min 
                      ? `₹${selectedApplication.expectedSalary.min.toLocaleString('en-IN')}/month`
                      : selectedApplication.candidateProfile?.expectedSalary 
                      ? `₹${selectedApplication.candidateProfile.expectedSalary.toLocaleString('en-IN')}/month`
                      : 'Not specified'}
                  </Typography>
                  <Typography variant="body2">
                    Available From: {selectedApplication.availableFrom 
                      ? formatDate(selectedApplication.availableFrom)
                      : selectedApplication.candidateProfile?.availableDate
                      ? formatDate(selectedApplication.candidateProfile.availableDate)
                      : 'Immediately'}
                  </Typography>
                </Grid>
                {selectedApplication.coverLetter && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Cover Letter</Typography>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body2">
                        {selectedApplication.coverLetter || selectedApplication.candidateProfile?.coverLetter}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
};

export default ManageApplications;
